import type { HttpClient } from '../http/HttpClient.ts';
import type { Comment } from './CollabApi.ts';

export type DecisionStatus = 'open' | 'decided';
export type DecisionMode = 'any' | 'specific' | 'vote' | 'unanimous' | 'majority';
export type DecisionStance = 'agree' | 'object';

export interface Named {
  id: string;
  name: string;
}

export interface DecisionActor {
  id: string | null;
  name: string;
  agent: Named | null;
}

export interface Readiness {
  /** Deciders needed: votes on one option, or agreements (null: nobody decides it yet). */
  needed: number | null;
  deciderCount: number;
  /** Deciders who've voted, or answered the proposal as it stands. */
  votedDeciders: number;
  /** Sign-off: deciders who agree (null in a vote). */
  agreedDeciders: number | null;
  /** Vote: the option that reached it. */
  readyOptionId: string | null;
  /** A decider can decide it now. */
  ready: boolean;
}

export interface DecisionSummary {
  id: string;
  number: number;
  title: string;
  status: DecisionStatus;
  group: Named | null;
  mode: DecisionMode;
  threshold: number | null;
  /**
   * Who decides: named people, its group's owners, or anyone who can edit (in a
   * sign-off, everyone who can edit, all listed).
   */
  deciders: { from: 'named' | 'group' | 'everyone' | 'anyone'; people: Named[] };
  personOnly: boolean;
  askedBy: DecisionActor | null;
  decidedBy: DecisionActor | null;
  decidedAt: string | null;
  preview: string | null;
  readiness: Readiness | null;
  waitingOnYou: boolean;
  blocking: number;
  createdAt: string;
  updatedAt: string;
}

export interface DecisionOption {
  id: string;
  label: string;
  detail: string;
  deciderVotes: Named[];
  otherVotes: Named[];
}

export interface Objection extends Named {
  reason: string;
}

/** A sign-off's answers to the proposal as it stands (Unanimous, Majority). */
export interface Signoff {
  /** Sent back with an answer: which wording you read. */
  proposalHash: string;
  agree: { deciders: Named[]; others: Named[] };
  object: { deciders: Objection[]; others: Objection[] };
  /** Answered an earlier wording, so asked again. */
  earlier: Named[];
  yours: { stance: DecisionStance; reason: string | null } | null;
}

export interface DecisionVersion {
  version: number;
  title: string;
  questionText: string;
  decisionText: string;
  decidedBy: DecisionActor | null;
  decidedAt: string;
  basis: string | null;
  revisedBy: Named | null;
  revisedAt: string | null;
  reason: string | null;
}

export interface DecisionDetail extends DecisionSummary {
  questionText: string;
  decisionText: string;
  basis: string | null;
  version: number;
  options: DecisionOption[];
  yourVote: string | null;
  chosenOptionId: string | null;
  signoff: Signoff | null;
  blocks: { id: string; number: number; title: string; type: string; statusCategory: string }[];
  history: DecisionVersion[];
  can: {
    edit: boolean;
    decide: boolean;
    revise: boolean;
    delete: boolean;
    vote: boolean;
    agree: boolean;
  };
}

export interface DecisionGroup {
  id: string;
  name: string;
  position: number;
  defaultMode: DecisionMode;
  defaultThreshold: number | null;
  owners: Named[];
  open: number;
}

export interface OptionInput {
  id: string | null;
  label: string;
  detail: string;
}

export interface NewDecision {
  title: string;
  details: string;
  groupId: string | null;
  /** null: the group's default (Any decider when ungrouped). */
  mode: DecisionMode | null;
  threshold: number | null;
  deciderIds: string[];
  personOnly: boolean;
  options: OptionInput[];
  blocks: number[];
}

export interface DecisionPatch {
  title?: string;
  groupId?: string | null;
  mode?: DecisionMode;
  threshold?: number | null;
  deciderIds?: string[];
  personOnly?: boolean;
  options?: OptionInput[];
}

export interface GroupInput {
  name: string;
  ownerIds: string[];
  defaultMode: DecisionMode;
  defaultThreshold: number | null;
}

/** A project's decisions (/orgs/:org/projects/:key/decisions). */
export class DecisionsApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  #base(org: string, key: string): string {
    return `/orgs/${encodeURIComponent(org)}/projects/${encodeURIComponent(key)}/decisions`;
  }

  list(
    org: string,
    key: string,
    query: {
      status: DecisionStatus;
      q: string | null;
      group: string | null;
      before: number | null;
    },
  ): Promise<{ decisions: DecisionSummary[]; next: number | null }> {
    return this.#http.get(this.#base(org, key), {
      query: {
        status: query.status,
        ...(query.q ? { q: query.q } : {}),
        ...(query.group ? { group: query.group } : {}),
        ...(query.before !== null ? { before: query.before } : {}),
      },
    });
  }

  waiting(org: string, key: string): Promise<{ decisions: DecisionSummary[] }> {
    return this.#http.get(`${this.#base(org, key)}/waiting`);
  }

  counts(org: string, key: string): Promise<{ open: number; waitingOnYou: number }> {
    return this.#http.get(`${this.#base(org, key)}/counts`);
  }

  get(org: string, key: string, n: number): Promise<{ decision: DecisionDetail }> {
    return this.#http.get(`${this.#base(org, key)}/${n}`);
  }

  create(org: string, key: string, input: NewDecision): Promise<{ decision: DecisionDetail }> {
    return this.#http.post(this.#base(org, key), input);
  }

  update(
    org: string,
    key: string,
    n: number,
    patch: DecisionPatch,
  ): Promise<{ decision: DecisionDetail }> {
    return this.#http.patch(`${this.#base(org, key)}/${n}`, patch);
  }

  decide(
    org: string,
    key: string,
    n: number,
    input: { decision: string; optionId: string | null; basis: string | null },
  ): Promise<{ decision: DecisionDetail }> {
    return this.#http.post(`${this.#base(org, key)}/${n}/decide`, input);
  }

  revise(
    org: string,
    key: string,
    n: number,
    reason: string,
  ): Promise<{ decision: DecisionDetail }> {
    return this.#http.post(`${this.#base(org, key)}/${n}/revise`, { reason });
  }

  remove(org: string, key: string, n: number): Promise<void> {
    return this.#http.delete(`${this.#base(org, key)}/${n}`);
  }

  vote(
    org: string,
    key: string,
    n: number,
    optionId: string,
  ): Promise<{ decision: DecisionDetail }> {
    return this.#http.put(`${this.#base(org, key)}/${n}/vote`, { optionId });
  }

  unvote(org: string, key: string, n: number): Promise<void> {
    return this.#http.delete(`${this.#base(org, key)}/${n}/vote`);
  }

  answer(
    org: string,
    key: string,
    n: number,
    input: { stance: DecisionStance; reason: string | null; proposalHash: string },
  ): Promise<{ decision: DecisionDetail }> {
    return this.#http.put(`${this.#base(org, key)}/${n}/agreement`, input);
  }

  withdraw(org: string, key: string, n: number): Promise<void> {
    return this.#http.delete(`${this.#base(org, key)}/${n}/agreement`);
  }

  block(org: string, key: string, n: number, items: number[]): Promise<void> {
    return this.#http.post(`${this.#base(org, key)}/${n}/blocks`, { items });
  }

  unblock(org: string, key: string, n: number, item: number): Promise<void> {
    return this.#http.delete(`${this.#base(org, key)}/${n}/blocks/${item}`);
  }

  comments(
    org: string,
    key: string,
    n: number,
    before: string | null,
  ): Promise<{ comments: Comment[]; earlier: string | null }> {
    return this.#http.get(`${this.#base(org, key)}/${n}/comments`, {
      query: before === null ? {} : { before },
    });
  }

  comment(org: string, key: string, n: number, text: string): Promise<{ comment: Comment }> {
    return this.#http.post(`${this.#base(org, key)}/${n}/comments`, { text });
  }

  groups(org: string, key: string): Promise<{ groups: DecisionGroup[] }> {
    return this.#http.get(`${this.#base(org, key)}/groups`);
  }

  createGroup(org: string, key: string, input: GroupInput): Promise<{ group: DecisionGroup }> {
    return this.#http.post(`${this.#base(org, key)}/groups`, input);
  }

  updateGroup(
    org: string,
    key: string,
    id: string,
    patch: Partial<GroupInput>,
  ): Promise<{ group: DecisionGroup }> {
    return this.#http.patch(`${this.#base(org, key)}/groups/${encodeURIComponent(id)}`, patch);
  }

  reorderGroups(org: string, key: string, ids: string[]): Promise<{ groups: DecisionGroup[] }> {
    return this.#http.put(`${this.#base(org, key)}/groups/order`, { ids });
  }

  removeGroup(org: string, key: string, id: string): Promise<void> {
    return this.#http.delete(`${this.#base(org, key)}/groups/${encodeURIComponent(id)}`);
  }
}
